import SuccessImg from "@/assets/being-vip.svg";
import FailedImg from "@/assets/strategy.svg";
import { Button, Loading } from "@/components";
import { QuizDto } from "@/types";
import { cn } from "@/utils";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuizRelated, useQuizResult } from "../hooks/useQuizDetail";
import { DoneQuizModal } from "./DoneQuizModal";
import { QuizCard } from "./QuizCard";
import { QuizModal } from "./QuizModal";

const QuizResult = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuizResult(id ? Number(id) : undefined);

  const { data: relatedQuizData, isLoading: isLoadingRelatedQuiz } =
    useQuizRelated(id ? Number(id) : undefined);

  const result = useMemo(() => {
    return {
      score: data?.data?.quizz.marks_obtained || 0,
      totalQuestions: data?.data?.quizz.total_questions || 0,
      passed: data?.data?.quizz.result === "PASSED",
    };
  }, [id, data]);

  const otherQuizzes = useMemo(() => {
    return (relatedQuizData?.data?.map((quiz) => ({
      id: quiz.id,
      title: quiz.title,
      image: quiz.image,
      result: quiz.result,
      marks_obtained: quiz.marks_obtained,
      total_questions: quiz.total_questions,
      user_quiz_id: quiz.user_quiz_id,
    })) || []) as QuizDto[];
  }, [id, relatedQuizData]);

  if (isLoading || isLoadingRelatedQuiz) {
    return <Loading />;
  }

  return (
    <div className="container mx-auto max-w-5xl px-4 py-4">
      <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-6">
        <div className="w-64 h-64">
          <img
            src={result.passed ? SuccessImg : FailedImg}
            className="w-full h-full"
          />
        </div>

        <h1 className="text-lg font-bold">
          {result.passed
            ? "テストに合格おめでとうございます！"
            : "おしい、合格点に届かなかったようです。"}
        </h1>

        <div className="text-center">
          <p className="text-base font-semibold mb-2">スコア</p>
          <span className="inline-flex text-4xl font-bold">
            <p
              className={cn(
                result.passed ? "text-success_lighter" : "text-error_main",
              )}
            >
              {result.score}
            </p>
            /<p>{result.totalQuestions}</p>
          </span>
        </div>

        {/* Buttons */}
        <div className="flex flex-col w-full max-w-xs gap-2">
          <DoneQuizModal
            key={data!.data!.quizz.id}
            quiz={{
              ...data!.data!.quizz,
              user_quiz_id: id ? Number(id) : undefined,
            }}
            trigger={
              <Button buttonType="primary" className="w-full">
                結果を見る
              </Button>
            }
          />
          {!result.passed && (
            <QuizModal
              quiz={data!.data!.quizz}
              trigger={
                <Button variant="outlined" color="primary" className="w-full">
                  テストをやり直す
                </Button>
              }
            />
          )}
          <Button
            variant="outlined"
            color="primary"
            className="w-full"
            onClick={() => navigate("/")}
          >
            トップページへ戻る
          </Button>
        </div>
      </div>

      {/* Other Test Results */}
      {otherQuizzes.length > 0 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold mb-6 text-center">
            他のテストの結果
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {otherQuizzes.map((quiz) => (
              <DoneQuizModal
                key={quiz.id}
                quiz={{
                  ...quiz,
                  user_quiz_id: quiz.user_quiz_id,
                }}
                trigger={
                  <QuizCard
                    key={quiz.id}
                    id={quiz.id}
                    title={quiz.title}
                    coverImage={quiz.image}
                    score={quiz.marks_obtained || 0}
                    passed={quiz.result === "PASSED"}
                    question_count={quiz.total_questions || 0}
                  />
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizResult;
